'use client';

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

function Icon({ name, className }: { name: string; className?: string }) {
  return <span className={cn('material-symbols-outlined', className)}>{name}</span>;
}

interface ConsentState {
  terms_of_service: boolean;
  data_processing: boolean;
  ai_processing: boolean;
  email_notifications: boolean;
  analytics: boolean;
}

const CONSENT_STORAGE_KEY = 'gdpr_consent_given';
const CONSENT_VERSION = '1.0';

const CONSENT_LABELS: Record<keyof ConsentState, { label: string; description: string; required: boolean }> = {
  terms_of_service: {
    label: 'Términos de Servicio',
    description: 'Aceptación de los términos y condiciones de uso de la plataforma.',
    required: true,
  },
  data_processing: {
    label: 'Procesamiento de Datos',
    description: 'Consentimiento para el procesamiento de tus datos personales y financieros según el RGPD (Art. 6).',
    required: true,
  },
  ai_processing: {
    label: 'Procesamiento por IA',
    description: 'Uso de inteligencia artificial para verificación de documentos y análisis financiero (Art. 22 RGPD).',
    required: false,
  },
  email_notifications: {
    label: 'Notificaciones por Email',
    description: 'Envío de alertas y notificaciones sobre tu cuenta y documentos.',
    required: false,
  },
  analytics: {
    label: 'Análisis de Uso',
    description: 'Recopilación de datos anónimos de uso para mejorar la plataforma.',
    required: false,
  },
};

export default function GDPRConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [consents, setConsents] = useState<ConsentState>({
    terms_of_service: false,
    data_processing: false,
    ai_processing: false,
    email_notifications: false,
    analytics: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) {
      setVisible(true);
    } else {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.version !== CONSENT_VERSION) {
          setVisible(true);
        }
      } catch {
        setVisible(true);
      }
    }
  }, []);

  const handleToggle = (key: keyof ConsentState) => {
    if (CONSENT_LABELS[key].required) return;
    setConsents(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAcceptAll = async () => {
    const allTrue: ConsentState = {
      terms_of_service: true,
      data_processing: true,
      ai_processing: true,
      email_notifications: true,
      analytics: true,
    };
    setConsents(allTrue);
    await saveConsents(allTrue);
  };

  const handleAcceptRequired = async () => {
    const requiredOnly: ConsentState = {
      terms_of_service: true,
      data_processing: true,
      ai_processing: consents.ai_processing,
      email_notifications: consents.email_notifications,
      analytics: consents.analytics,
    };
    setConsents(requiredOnly);
    await saveConsents(requiredOnly);
  };

  const saveConsents = async (state: ConsentState) => {
    setSaving(true);
    try {
      // Save to API (if user is authenticated)
      for (const [type, granted] of Object.entries(state)) {
        try {
          await fetch('/api/gdpr/consent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              consent_type: type,
              granted,
              version: CONSENT_VERSION,
            }),
          });
        } catch {
          // API call may fail if not authenticated yet - that's OK
        }
      }

      // Always save to localStorage as fallback
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        consents: state,
        timestamp: new Date().toISOString(),
      }));

      setVisible(false);
    } catch (error) {
      console.error('[GDPR] Error saving consents:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-calm-border shadow-2xl">
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <Icon name="shield" className="text-2xl text-calm-text" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-light font-display text-calm-text">
              Protección de Datos y Privacidad
            </h3>
            <p className="mt-1 text-sm text-calm-muted">
              Externaliza2 cumple con el Reglamento General de Protección de Datos (RGPD/GDPR). 
              Necesitamos tu consentimiento para procesar tus datos.{' '}
              <a href="/privacy-policy" className="text-calm-text hover:underline" target="_blank">
                Política de Privacidad completa
              </a>
            </p>

            {/* Expandable consent details */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-2 flex items-center gap-1 text-sm text-calm-text hover:text-calm-muted font-medium"
            >
              {expanded ? 'Ocultar detalles' : 'Personalizar consentimientos'}
              {expanded ? <Icon name="expand_less" className="text-base" /> : <Icon name="expand_more" className="text-base" />}
            </button>

            {expanded && (
              <div className="mt-3 space-y-3 border-t border-calm-border pt-3">
                {(Object.entries(CONSENT_LABELS) as [keyof ConsentState, typeof CONSENT_LABELS[keyof ConsentState]][]).map(
                  ([key, config]) => (
                    <label
                      key={key}
                      className={`flex items-start gap-3 p-2 rounded-2xl ${
                        config.required ? 'bg-slate-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={config.required ? true : consents[key]}
                        onChange={() => handleToggle(key)}
                        disabled={config.required}
                        className="mt-0.5 h-4 w-4 rounded border-calm-border text-calm-text focus:ring-calm-text/20 disabled:opacity-60"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-calm-text">
                          {config.label}
                          {config.required && (
                            <span className="ml-1 text-xs text-calm-accent font-normal">(obligatorio)</span>
                          )}
                        </span>
                        <p className="text-xs text-calm-accent mt-0.5">
                          {config.description}
                        </p>
                      </div>
                    </label>
                  )
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
          {expanded && (
            <button
              onClick={handleAcceptRequired}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-slate-50 text-calm-muted hover:bg-slate-100 rounded-2xl transition-colors disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar selección'}
            </button>
          )}
          <button
            onClick={handleAcceptAll}
            disabled={saving}
            className="px-6 py-2 text-sm font-medium text-white bg-calm-text rounded-2xl hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Aceptar todo'}
          </button>
        </div>

        {/* GDPR rights reminder */}
        <p className="mt-3 text-xs text-calm-accent text-center sm:text-right">
          Puedes modificar tus consentimientos en cualquier momento desde tu perfil.
          Tienes derecho a acceso, rectificación, supresión y portabilidad de tus datos (RGPD Art. 15-20).
        </p>
      </div>
    </div>
  );
}
